import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import moment from 'moment';
import { TABS } from './constants';

export const useAttendanceState = ({
    userId,
    userRowDetails,
    specificUserProfile,
    joinedDate,
    isStaffRoute,
    isTeacher,
    activeTab,
    holidays,
    storeWorkingDaysDetails,
    attendanceRequirement,
    setPageLoader,
    actions: {
        getHolidays,
        getTeacherAttendanceTab,
        getOperationAttendanceTab,
        getStudentAttendanceTab,
        attendanceRequirementDetail,
    }
}) => {
    const [attendanceData, setAttendanceData] = useState(null);
    const [studentAttendanceData, setStudentAttendanceData] = useState(null);
    const [selectedAttendanceCourseId, setSelectedAttendanceCourseId] = useState(null);
    const [selectedAttendanceDate, setSelectedAttendanceDate] = useState(() => moment().format("YYYY-MM-DD"));
    const [selectedDate] = useState(() => moment().format("YYYY-MM-DD"));
    const [attendanceLoading, setAttendanceLoading] = useState(false);

    const calendarScrollContainerRef = useRef(null);
    const currentMonthRef = useRef(null);
    const attendanceSummaryCardRef = useRef(null);
    const [attendanceSummaryHeight, setAttendanceSummaryHeight] = useState(null);

    const fetchAttendance = useCallback((onDone) => {
        const targetId = userId || userRowDetails?.id || specificUserProfile?.id;
        if (!targetId) {
            setAttendanceData(null);
            setStudentAttendanceData(null);
            onDone?.();
            return;
        }
        setAttendanceLoading(true);
        setPageLoader?.(true);

        if (isStaffRoute && isTeacher) {
            const todayStr = moment().format("YYYY-MM-DD");
            const thisYear = moment().format("YYYY");
            setSelectedAttendanceDate(todayStr);
            getHolidays?.({ userId: targetId, year: thisYear });
            getTeacherAttendanceTab?.(targetId, { date: todayStr, year: thisYear }, (response, error) => {
                setAttendanceLoading(false);
                setPageLoader?.(false);
                if (response && !error) {
                    const data = response?.data ?? response;
                    setStudentAttendanceData(data);
                    if (data?.selectedDate) {
                        setSelectedAttendanceDate(data.selectedDate);
                    }
                }
                onDone?.();
            });
            return;
        }

        if (isStaffRoute && !isTeacher) {
            const todayStr = moment().format("YYYY-MM-DD");
            const thisYear = moment().format("YYYY");
            setSelectedAttendanceDate(todayStr);
            getHolidays?.({ userId: targetId, year: thisYear });
            getOperationAttendanceTab?.(targetId, { date: todayStr, year: thisYear }, (response, error) => {
                setAttendanceLoading(false);
                setPageLoader?.(false);
                if (response && !error) {
                    const data = response?.data ?? response;
                    setStudentAttendanceData(data);
                    if (data?.selectedDate) {
                        setSelectedAttendanceDate(data.selectedDate);
                    }
                }
                onDone?.();
            });
            return;
        }

        getStudentAttendanceTab?.(targetId, {}, (response, error) => {
            setAttendanceLoading(false);
            setPageLoader?.(false);
            if (response && !error) {
                const data = response?.data ?? response;
                setStudentAttendanceData(data);
                if (data?.selectedCourseId) {
                    setSelectedAttendanceCourseId(data.selectedCourseId);
                }
                if (data?.selectedDate) {
                    setSelectedAttendanceDate(data.selectedDate);
                }
            }
            onDone?.();
        });
    }, [userId, userRowDetails, specificUserProfile, isStaffRoute, isTeacher, getTeacherAttendanceTab, getOperationAttendanceTab, getStudentAttendanceTab, getHolidays, setPageLoader]);

    const handleAttendanceDateClick = useCallback((dateStr) => {
        const targetId = userId || userRowDetails?.id || specificUserProfile?.id;
        if (!targetId) return;
        if (moment(dateStr).isAfter(moment(), 'day')) return;

        setSelectedAttendanceDate(dateStr);
        setAttendanceLoading(true);

        if (isStaffRoute && isTeacher) {
            const dateYear = moment(dateStr).format("YYYY");
            getHolidays?.({ userId: targetId, year: dateYear });
            getTeacherAttendanceTab?.(targetId, { date: dateStr, year: dateYear }, (response, error) => {
                setAttendanceLoading(false);
                if (response && !error) {
                    const data = response?.data ?? response;
                    setStudentAttendanceData(data);
                    if (data?.selectedDate) {
                        setSelectedAttendanceDate(data.selectedDate);
                    }
                }
            });
            return;
        }

        if (isStaffRoute && !isTeacher) {
            const dateYear = moment(dateStr).format("YYYY");
            getHolidays?.({ userId: targetId, year: dateYear });
            getOperationAttendanceTab?.(targetId, { date: dateStr, year: dateYear }, (response, error) => {
                setAttendanceLoading(false);
                if (response && !error) {
                    const data = response?.data ?? response;
                    setStudentAttendanceData(data);
                    if (data?.selectedDate) {
                        setSelectedAttendanceDate(data.selectedDate);
                    }
                }
            });
            return;
        }

        const courseId = selectedAttendanceCourseId || studentAttendanceData?.selectedCourseId;
        getStudentAttendanceTab?.(targetId, { courseId, date: dateStr }, (response, error) => {
            setAttendanceLoading(false);
            if (response && !error) {
                const data = response?.data ?? response;
                setStudentAttendanceData(data);
                if (data?.selectedCourseId) {
                    setSelectedAttendanceCourseId(data.selectedCourseId);
                }
            }
        });
    }, [userId, userRowDetails, specificUserProfile, isStaffRoute, isTeacher, getTeacherAttendanceTab, getOperationAttendanceTab, selectedAttendanceCourseId, studentAttendanceData, getStudentAttendanceTab, getHolidays]);

    const handleAttendanceCourseChange = useCallback((newCourseId) => {
        const targetId = userId || userRowDetails?.id || specificUserProfile?.id;
        if (!targetId) return;
        setSelectedAttendanceCourseId(newCourseId);
        setAttendanceLoading(true);
        getStudentAttendanceTab?.(targetId, { courseId: newCourseId, date: selectedAttendanceDate }, (response, error) => {
            setAttendanceLoading(false);
            if (response && !error) {
                const data = response?.data ?? response;
                setStudentAttendanceData(data);
                if (data?.selectedCourseId) {
                    setSelectedAttendanceCourseId(data.selectedCourseId);
                }
            }
        });
    }, [userId, userRowDetails, specificUserProfile, selectedAttendanceDate, getStudentAttendanceTab]);

    useEffect(() => {
        if (activeTab !== TABS[3].label && activeTab !== "Attendance") return;
        fetchAttendance();
    }, [activeTab, fetchAttendance]);

    useEffect(() => {
        if (activeTab === TABS[3].label || activeTab === "Attendance") {
            const timer = setTimeout(() => {
                if (currentMonthRef.current && calendarScrollContainerRef.current) {
                    const container = calendarScrollContainerRef.current;
                    const target = currentMonthRef.current;
                    const targetLeft = target.offsetLeft - container.offsetLeft;
                    container.scrollTo({
                        left: Math.max(0, targetLeft),
                        behavior: 'smooth'
                    });
                }
            }, 150);
            return () => clearTimeout(timer);
        }
    }, [activeTab]);

    const attendanceYear = useMemo(() => {
        if (selectedAttendanceDate) {
            const y = moment(selectedAttendanceDate).year();
            if (!isNaN(y) && y > 2000) return y;
        }
        return moment().year();
    }, [selectedAttendanceDate]);

    useEffect(() => {
        attendanceRequirementDetail?.();
    }, [attendanceRequirementDetail]);

    const activeOffDays = useMemo(() => {
        const reqSource = attendanceRequirement?.data || attendanceRequirement || {};
        const offList = Array.isArray(reqSource?.offDays)
            ? reqSource.offDays
            : (Array.isArray(attendanceRequirement?.offDays) ? attendanceRequirement.offDays : []);
        return offList.map(d => String(d).trim().toLowerCase());
    }, [attendanceRequirement]);

    const activeWorkingDays = useMemo(() => {
        const reqSource = attendanceRequirement?.data || attendanceRequirement || {};
        const reqWd = Array.isArray(reqSource?.workingDays)
            ? reqSource.workingDays
            : (Array.isArray(attendanceRequirement?.workingDays) ? attendanceRequirement.workingDays : []);
        if (reqWd.length > 0) {
            return reqWd.map(d => String(d).trim().toLowerCase());
        }
        if (Array.isArray(storeWorkingDaysDetails) && storeWorkingDaysDetails.length > 0) {
            return storeWorkingDaysDetails.map(d => String(d).trim().toLowerCase());
        }
        if (Array.isArray(holidays?.workingDays) && holidays.workingDays.length > 0) {
            return holidays.workingDays.map(d => String(d).trim().toLowerCase());
        }
        return [];
    }, [attendanceRequirement, storeWorkingDaysDetails, holidays?.workingDays]);

    const attendanceCalendarMap = useMemo(() => {
        const map = {};
        if (Array.isArray(studentAttendanceData?.calendar)) {
            studentAttendanceData.calendar.forEach(item => {
                if (item?.date) {
                    map[item.date] = item;
                }
            });
        }
        return map;
    }, [studentAttendanceData]);

    useEffect(() => {
        const targetId = userId || userRowDetails?.id || specificUserProfile?.id;
        if (isStaffRoute && targetId && (activeTab === TABS[3].label || activeTab === "Attendance")) {
            getHolidays?.({ userId: targetId, year: attendanceYear });
        }
    }, [isStaffRoute, userId, userRowDetails?.id, specificUserProfile?.id, activeTab, attendanceYear, getHolidays]);

    // Holidays from the attendance-tab API (`holidays: [{ date, title }]`) —
    // past and future. Staff only.
    const apiHolidayMap = useMemo(() => {
        const map = {};
        if (isStaffRoute && Array.isArray(studentAttendanceData?.holidays)) {
            studentAttendanceData.holidays.forEach(h => {
                if (h?.date) map[moment(h.date).format('YYYY-MM-DD')] = h.title || '';
            });
        }
        return map;
    }, [isStaffRoute, studentAttendanceData]);

    // Approved leaves from the attendance-tab API (`leaves: [{ date, leaveType, status }]`)
    // — past and future, shown like holidays. Staff only.
    const apiLeaveMap = useMemo(() => {
        const map = {};
        if (isStaffRoute && Array.isArray(studentAttendanceData?.leaves)) {
            studentAttendanceData.leaves.forEach(l => {
                if (l?.date && String(l.status || '').toLowerCase() === 'approved') {
                    map[moment(l.date).format('YYYY-MM-DD')] = l.leaveType || '';
                }
            });
        }
        return map;
    }, [isStaffRoute, studentAttendanceData]);

    const dynamicCalendarMonths = useMemo(() => {
        const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
        const effectiveHolidayList = holidays?.holidayList || [];
        const currentMoment = moment();

        return monthNames.map((name, monthIndex) => {
            const daysInMonth = new Date(attendanceYear, monthIndex + 1, 0).getDate();
            const firstWeekday = (new Date(attendanceYear, monthIndex, 1).getDay() + 6) % 7;
            const days = Array.from({ length: firstWeekday }, () => ({ empty: true }));

            const monthData = effectiveHolidayList?.find(m =>
                m?.month?.toLowerCase() === name?.toLowerCase() ||
                moment(m?.month, 'MMMM').month() === monthIndex
            );

            for (let day = 1; day <= daysInMonth; day++) {
                const dateStr = `${attendanceYear}-${String(monthIndex + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                const dayDate = new Date(attendanceYear, monthIndex, day);
                const dayMoment = moment(dayDate);
                const dayOfWeek = dayDate.getDay();
                const dayNameLower = dayMoment.format('dddd').toLowerCase();

                const calItem = attendanceCalendarMap[dateStr];
                let itemStatus = calItem?.status;
                const clockIn = calItem?.clockIn;
                const clockOut = calItem?.clockOut;
                const isFuture = dayMoment.isAfter(currentMoment, 'day');

                let isDayOff = false;

                const statusLower = String(itemStatus || '').toLowerCase().replace(/[\s_-]/g, '');
                const isExplicitCancelled = statusLower === 'cancelled' || statusLower === 'classcancelled';
                const isExplicitOffdayStatus = statusLower === 'offday' || statusLower === 'weeklyoff';
                // Staff only: Holiday / Leave from the API are real statuses
                // too — they must not be overridden to "offday" on a
                // weekly-off day. Students keep the original behaviour.
                const isStaffHolidayOrLeave = isStaffRoute
                    && (statusLower === 'holiday' || statusLower === 'leave' || statusLower === 'onleave');
                const hasActiveAttendance = statusLower === 'present' || statusLower === 'absent' || statusLower === 'partial' || statusLower === 'partiallypresent'
                    || isStaffHolidayOrLeave;

                const holidayMatch = monthData?.data?.find(h => {
                    const hDate = moment(h?.date).format('YYYY-MM-DD');
                    return hDate === dateStr && (h?.selected || h?.saved === 1 || !holidays?.edit);
                });

                const isExplicitHoliday = Boolean(holidayMatch && holidayMatch?.type?.toLowerCase() !== 'offday' && !holidayMatch?.isOffDay);

                const apiHolidayTitle = apiHolidayMap[dateStr];
                const isApiHoliday = apiHolidayTitle !== undefined;
                // Listed holiday, and no present / partial / leave recorded that day -> Holiday.
                const hasWorkedOrLeave = statusLower === 'present' || statusLower === 'partial'
                    || statusLower === 'partiallypresent' || statusLower === 'leave' || statusLower === 'onleave';
                const showApiHoliday = isApiHoliday && !hasWorkedOrLeave;
                if (showApiHoliday) {
                    itemStatus = 'holiday';
                }

                // Approved leave, unless that day was worked (present / partial) or is a holiday.
                const apiLeaveType = apiLeaveMap[dateStr];
                const hasWorked = statusLower === 'present' || statusLower === 'partial' || statusLower === 'partiallypresent';
                const showApiLeave = apiLeaveType !== undefined && !hasWorked && !showApiHoliday;
                if (showApiLeave) {
                    itemStatus = 'leave';
                }

                // Whatever the API sent for this date (calendar status or leave) is
                // always shown. Before the user joined, dates with no such data —
                // and holidays — are shown disabled like future dates.
                const isCalendarHoliday = statusLower === 'holiday';
                const hasApiData = (Boolean(calItem?.status) && !isCalendarHoliday) || showApiLeave;
                const isBeforeJoin = !hasApiData && Boolean(joinedDate) && dayMoment.isBefore(moment(joinedDate), 'day');

                const isExplicitOffday = Boolean(
                    holidayMatch?.type?.toLowerCase() === 'offday' ||
                    holidayMatch?.isOffDay ||
                    (Array.isArray(holidays?.offDays) && holidays.offDays.includes(dateStr)) ||
                    (Array.isArray(studentAttendanceData?.offDays) && studentAttendanceData.offDays.includes(dateStr))
                );

                let isWeeklyOff = false;
                if (activeOffDays.length > 0 || activeWorkingDays.length > 0) {
                    const matchesOffDay = activeOffDays.length > 0 && activeOffDays.includes(dayNameLower);
                    const notWorkingDay = activeWorkingDays.length > 0 && !activeWorkingDays.includes(dayNameLower);
                    isWeeklyOff = matchesOffDay || notWorkingDay;
                } else {
                    isWeeklyOff = (dayOfWeek === 0 || dayOfWeek === 6);
                }

                // Students: a cancelled class is its own status ("Class cancelled"), not a day off.
                if (isExplicitCancelled && !isStaffRoute) {
                    itemStatus = 'classcancelled';
                } else if (isExplicitCancelled || isExplicitOffdayStatus) {
                    isDayOff = true;
                    itemStatus = 'offday';
                } else if (!hasActiveAttendance && !isExplicitHoliday && !showApiHoliday && !showApiLeave && (isExplicitOffday || isWeeklyOff)) {
                    isDayOff = true;
                    itemStatus = 'offday';
                }

                if (isBeforeJoin) {
                    itemStatus = null;
                    isDayOff = false;
                }

                days.push({
                    day,
                    dateStr,
                    status: itemStatus,
                    clockIn,
                    clockOut,
                    selected: dateStr === selectedAttendanceDate,
                    isToday: dateStr === currentMoment.format("YYYY-MM-DD"),
                    isFuture: isFuture || isBeforeJoin,
                    isOffDay: isDayOff,
                    holidayTitle: isApiHoliday && !isBeforeJoin ? apiHolidayTitle : null,
                    leaveType: showApiLeave && !isBeforeJoin ? apiLeaveType : null,
                });
            }
            return {
                title: `${name} ${attendanceYear}`,
                monthIndex,
                days,
            };
        });
    }, [attendanceYear, attendanceCalendarMap, selectedAttendanceDate, isStaffRoute, holidays, activeWorkingDays, activeOffDays, studentAttendanceData, apiHolidayMap, apiLeaveMap, joinedDate]);

    return {
        attendanceData,
        studentAttendanceData,
        selectedAttendanceCourseId,
        selectedAttendanceDate,
        selectedDate,
        attendanceLoading,
        calendarScrollContainerRef,
        currentMonthRef,
        attendanceSummaryCardRef,
        attendanceSummaryHeight,
        setAttendanceSummaryHeight,
        dynamicCalendarMonths,
        fetchAttendance,
        handleAttendanceDateClick,
        handleAttendanceCourseChange,
    };
};
